import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MapPin, Route, Play, CheckCircle, ChevronUp, ChevronDown, GripVertical, Search, Camera } from 'lucide-react'
import Swal from 'sweetalert2'
import api from '../../Includes/api/api'
import { bindKarnatakaAutocomplete, liveLocationIcon, loadGoogleMaps, watchLiveLocation } from '../../Includes/googleMaps'
import { useSignupAdmin } from '../../Includes/signupAdmin'

const DEFAULT_CENTER = { lat: 12.9716, lng: 77.5946 }
const PERSON_COLORS = ['#1a73e8', '#ea580c', '#16a34a', '#7c3aed', '#db2777', '#0891b2']

const schoolName = (areaName) => (areaName || '').split(',')[0].trim() || 'Visit'

const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')

const todayIso = () => {
  const date = new Date()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

const formatTime = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

const listFrom = (payload) => {
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.results)) return payload.results
  if (Array.isArray(payload)) return payload
  return []
}

const stopPosition = (visit) => {
  const lat = Number(visit.latitude)
  const lng = Number(visit.longitude)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat, lng }
}

const sortVisits = (rows) => [...rows].sort((a, b) => {
  const aOrder = a.route_order == null ? null : Number(a.route_order)
  const bOrder = b.route_order == null ? null : Number(b.route_order)
  if (aOrder != null && bOrder != null && aOrder !== bOrder) return aOrder - bOrder
  if (aOrder != null && bOrder == null) return -1
  if (aOrder == null && bOrder != null) return 1
  const aTime = a.start_time || a.school_arrival_time || ''
  const bTime = b.start_time || b.school_arrival_time || ''
  if (aTime && bTime && aTime !== bTime) return aTime < bTime ? -1 : 1
  if (aTime && !bTime) return -1
  if (!aTime && bTime) return 1
  return (a.id || 0) - (b.id || 0)
})

const haversineKm = (lat1, lng1, lat2, lng2) => {
  const toRad = (value) => (value * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

const dataUrlToBlob = (dataUrl) => {
  const [header, b64] = dataUrl.split(',')
  const mime = (header.match(/:(.*?);/) || [])[1] || 'image/jpeg'
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

const mapsQueryUrl = (lat, lng, fallbackName) => {
  if (lat != null && lng != null && Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))) {
    return `https://www.google.com/maps/search/?api=1&query=${Number(lat)},${Number(lng)}`
  }
  const name = (fallbackName || '').trim()
  if (!name) return ''
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}`
}

const stampPhotoCaption = (ctx, width, height, lines) => {
  const fontSize = Math.max(13, Math.round(width / 32))
  ctx.font = `600 ${fontSize}px sans-serif`
  const pad = 8
  const lineHeight = fontSize + 5
  const boxHeight = lines.length * lineHeight + pad * 2
  ctx.fillStyle = 'rgba(15, 23, 42, 0.75)'
  ctx.fillRect(0, height - boxHeight, width, boxHeight)
  ctx.fillStyle = '#ffffff'
  const maxWidth = width - pad * 2
  lines.forEach((line, index) => {
    let text = line
    while (text.length > 8 && ctx.measureText(text).width > maxWidth) text = `${text.slice(0, -2)}…`
    ctx.fillText(text, pad, height - boxHeight + pad + fontSize + index * lineHeight)
  })
}

const shareOnWhatsApp = async ({ file, text, title }) => {
  try {
    if (file && navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], text, title })
      return
    }
  } catch (err) {
    if (err?.name === 'AbortError') return
  }
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener')
}

function LocationSearch({ onPlace }) {
  const inputRef = useRef(null)
  const onPlaceRef = useRef(onPlace)
  useEffect(() => { onPlaceRef.current = onPlace }, [onPlace])
  useEffect(() => {
    let cancelled = false
    loadGoogleMaps().then((maps) => {
      if (cancelled || !inputRef.current) return
      bindKarnatakaAutocomplete(maps, inputRef.current, (place) => onPlaceRef.current(place))
    }).catch(() => {})
    return () => {
      cancelled = true
      inputRef.current?._karnatakaCleanup?.()
    }
  }, [])
  return (
    <div style={{ position: 'relative' }}>
      <Search size={15} style={{ position: 'absolute', left: 10, top: 11, color: '#64748b', pointerEvents: 'none' }} />
      <input
        ref={inputRef}
        type="search"
        placeholder="Search location"
        style={{
          width: '100%',
          boxSizing: 'border-box',
          border: 0,
          borderRadius: 8,
          padding: '10px 12px 10px 32px',
          fontSize: 14,
          boxShadow: '0 1px 4px rgba(0,0,0,.25)',
          outline: 'none',
        }}
      />
    </div>
  )
}

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 768px)').matches)
  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)')
    const onChange = () => setIsMobile(media.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])
  return isMobile
}

export default function UnplannedMap() {
  const mapNodeRef = useRef(null)
  const mapRef = useRef(null)
  const overlaysRef = useRef([])
  const [mapError, setMapError] = useState('')
  const [mapReady, setMapReady] = useState(false)
  const [visits, setVisits] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [persons, setPersons] = useState([])
  const [personId, setPersonId] = useState('')
  const [academicYearId, setAcademicYearId] = useState('')
  const [saving, setSaving] = useState(false)
  const [selectedStopId, setSelectedStopId] = useState(null)
  const [orderOpen, setOrderOpen] = useState(false)
  const [orderDraft, setOrderDraft] = useState(null)
  const [draggingId, setDraggingId] = useState(null)
  const dragState = useRef({ dragId: null, list: null })
  const isMobile = useIsMobile()
  const draftMarkerRef = useRef(null)
  const schoolMarkersRef = useRef([])
  const hereMarkerRef = useRef(null)
  const [locating, setLocating] = useState(false)
  const [schools, setSchools] = useState([])
  const [cameraOpen, setCameraOpen] = useState(false)
  const [cameraPurpose, setCameraPurpose] = useState('')
  const [cameraVisit, setCameraVisit] = useState(null)
  const [capturedPhoto, setCapturedPhoto] = useState(null)
  const [cameraError, setCameraError] = useState('')
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const photoRequestRef = useRef(null)
  const cameraGpsRef = useRef({})
  const fileInputRef = useRef(null)
  const visitsRef = useRef([])
  const academicYearRef = useRef('')
  visitsRef.current = visits
  academicYearRef.current = academicYearId

  const isAdmin = useSignupAdmin()

  useEffect(() => {
    let cancelled = false
    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !mapNodeRef.current || mapRef.current) return
        mapRef.current = new maps.Map(mapNodeRef.current, {
          center: DEFAULT_CENTER,
          zoom: 12,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          gestureHandling: 'greedy',
        })
        setMapReady(true)
      })
      .catch((err) => {
        if (!cancelled) setMapError(err.message || 'Could not load Google Maps.')
      })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!mapReady) return undefined
    return watchLiveLocation(mapRef.current, window.google?.maps, hereMarkerRef)
  }, [mapReady])

  useEffect(() => {
    api.get('/basic/academicyear/').then((res) => {
      const years = listFrom(res.data)
      const activeYear = years.find((year) => year.is_active) || years[0]
      setAcademicYearId(activeYear?.id ? String(activeYear.id) : '')
    }).catch(() => setAcademicYearId(''))

    if (!isAdmin) return
    api.get('/persons/').then((res) => setPersons(listFrom(res.data))).catch(() => setPersons([]))
  }, [isAdmin])

  const loadVisits = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = { date: todayIso() }
      if (academicYearId) params.academic_year_id = academicYearId
      if (isMobile) params.mine = '1'
      else if (personId) params.person_id = personId
      const res = await api.get('/visits/today-map/', { params })
      const rows = Array.isArray(res.data?.visits) ? res.data.visits : []
      setVisits(sortVisits(rows.filter((visit) => visit.area_name !== 'From Home' && visit.area_name !== 'Lunch Break')))
    } catch (err) {
      setVisits([])
      setError(err?.response?.data?.detail || 'Could not load today’s visits.')
    } finally {
      setLoading(false)
    }
  }, [academicYearId, personId, isMobile])

  useEffect(() => {
    loadVisits()
  }, [loadVisits])

  const grouped = useMemo(() => {
    const groups = []
    const indexByPerson = {}
    visits.forEach((visit) => {
      const key = String(visit.person_id || 'unknown')
      if (indexByPerson[key] == null) {
        indexByPerson[key] = groups.length
        groups.push({
          personId: visit.person_id,
          personName: visit.person_name || 'Sales person',
          color: PERSON_COLORS[groups.length % PERSON_COLORS.length],
          visits: [],
        })
      }
      groups[indexByPerson[key]].visits.push(visit)
    })
    groups.forEach((group) => {
      group.visits = sortVisits(group.visits)
    })
    return groups
  }, [visits])

  useEffect(() => {
    const map = mapRef.current
    const maps = window.google?.maps
    if (!map || !maps || !mapReady) return undefined

    let cancelled = false
    overlaysRef.current.forEach((overlay) => overlay.setMap(null))
    overlaysRef.current = []
    const bounds = new maps.LatLngBounds()
    let hasPoint = false
    const info = new maps.InfoWindow()

    const addMarker = (position, label, title, html, color) => {
      const marker = new maps.Marker({
        map,
        position,
        title,
        label: {
          text: String(label),
          color: '#ffffff',
          fontSize: '12px',
          fontWeight: '700',
        },
        icon: {
          path: maps.SymbolPath.CIRCLE,
          scale: 14,
          fillColor: color,
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
      })
      marker.addListener('click', () => {
        info.setContent(html)
        info.open({ map, anchor: marker })
      })
      overlaysRef.current.push(marker)
      bounds.extend(position)
      hasPoint = true
    }

    grouped.forEach((group) => {
      const stops = []
      const gps = []
      group.visits.forEach((visit, index) => {
        const position = stopPosition(visit)
        if (position) {
          stops.push(position)
          const when = formatTime(visit.start_time || visit.school_arrival_time)
          addMarker(
            position,
            index + 1,
            schoolName(visit.area_name),
            `<div style="font-family:Roboto,Arial,sans-serif;max-width:220px">
              <div style="font-weight:700">${escapeHtml(schoolName(visit.area_name))}</div>
              <div style="color:#5f6368;font-size:12px;margin-top:4px">${escapeHtml(group.personName)}</div>
              <div style="font-size:12px;margin-top:4px">${when ? `${escapeHtml(when)} · ` : ''}${escapeHtml(visit.status || '')}</div>
            </div>`,
            group.color,
          )
        }
        ;(visit.path || []).forEach((point) => {
          const lat = Number(point.latitude)
          const lng = Number(point.longitude)
          if (Number.isFinite(lat) && Number.isFinite(lng)) gps.push({ lat, lng })
        })
      })

      if (gps.length >= 2) {
        overlaysRef.current.push(new maps.Polyline({
          map,
          path: gps,
          strokeColor: group.color,
          strokeOpacity: 0.45,
          strokeWeight: 7,
        }))
        gps.forEach((point) => bounds.extend(point))
        hasPoint = true
      }
      if (stops.length >= 2) {
        const renderer = new maps.DirectionsRenderer({
          map,
          suppressMarkers: true,
          preserveViewport: true,
          polylineOptions: {
            strokeColor: group.color,
            strokeOpacity: 0.9,
            strokeWeight: 5,
          },
        })
        overlaysRef.current.push(renderer)
        const service = new maps.DirectionsService()
        const middle = stops.slice(1, -1).slice(0, 23)
        service.route({
          origin: stops[0],
          destination: stops[stops.length - 1],
          waypoints: middle.map((location) => ({ location, stopover: true })),
          travelMode: maps.TravelMode.DRIVING,
        }, (result, status) => {
          if (cancelled) return
          if (status === 'OK' && result) {
            renderer.setDirections(result)
            return
          }
          const line = new maps.Polyline({
            map,
            path: stops,
            strokeColor: group.color,
            strokeOpacity: 0.9,
            strokeWeight: 4,
          })
          overlaysRef.current.push(line)
        })
      }
    })

    if (hasPoint) map.fitBounds(bounds, 60)
    else map.setCenter(DEFAULT_CENTER)

    return () => {
      cancelled = true
      overlaysRef.current.forEach((overlay) => overlay.setMap(null))
      overlaysRef.current = []
    }
  }, [grouped, mapReady])

  const readGps = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('GPS is not available on this phone.'))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({
        latitude: position.coords.latitude.toFixed(7),
        longitude: position.coords.longitude.toFixed(7),
        accuracy: position.coords.accuracy,
      }),
      () => reject(new Error('Turn on location, then try again.')),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
    )
  })

  const askTransport = async (title) => {
    const { value } = await Swal.fire({
      title,
      html: `
        <div style="text-align:left">
          <label style="display:block;font-size:13px;font-weight:600;margin-bottom:6px">Transport</label>
          <select id="unplanned-mode" class="swal2-select" style="width:100%;margin:0">
            <option value="bike">Bike</option>
            <option value="bus">Bus</option>
          </select>
          <div id="unplanned-charge-wrap" style="display:none;margin-top:10px">
            <label style="display:block;font-size:13px;font-weight:600;margin-bottom:6px">Bus charge (₹)</label>
            <input id="unplanned-charge" type="number" class="swal2-input" style="width:100%;margin:0" />
          </div>
        </div>`,
      didOpen: () => {
        const mode = document.getElementById('unplanned-mode')
        const wrap = document.getElementById('unplanned-charge-wrap')
        mode?.addEventListener('change', () => {
          wrap.style.display = mode.value === 'bus' ? 'block' : 'none'
        })
      },
      showCancelButton: true,
      confirmButtonText: 'Continue',
      preConfirm: () => {
        const mode = document.getElementById('unplanned-mode').value
        const charge = document.getElementById('unplanned-charge').value
        if (mode === 'bus' && !charge) {
          Swal.showValidationMessage('Enter the bus charge')
          return false
        }
        return { transport_mode: mode, bus_charge: mode === 'bus' ? parseFloat(charge) : 0 }
      },
    })
    return value || null
  }

  const myVisits = grouped[0]?.visits || []
  const orderList = orderDraft || myVisits

  const orderLocked = (visit) => visit?.status === 'visited' || visit?.status === 'riding_home'

  const applyOrder = async (reordered) => {
    const ids = reordered.map((visit) => visit.id)
    if (ids.join(',') === myVisits.map((visit) => visit.id).join(',')) return
    setVisits((current) => current.map((visit) => {
      const order = ids.indexOf(visit.id)
      return order === -1 ? visit : { ...visit, route_order: order + 1 }
    }))
    try {
      await api.post('/visits/today-order/', { ids })
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save the new order.')
      loadVisits()
    }
  }

  const moveVisit = async (index, direction) => {
    const source = orderDraft || myVisits
    const next = index + direction
    if (next < 0 || next >= source.length) return
    if (orderLocked(source[index]) || orderLocked(source[next])) return
    const reordered = [...source]
    const [item] = reordered.splice(index, 1)
    reordered.splice(next, 0, item)
    await applyOrder(reordered)
  }

  const onOrderPointerDown = (event, visit) => {
    if (orderLocked(visit) || saving) return
    event.preventDefault()
    const list = [...(orderDraft || myVisits)]
    dragState.current = { dragId: visit.id, list }
    setDraggingId(visit.id)
    setOrderDraft(list)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onOrderPointerMove = (event) => {
    const state = dragState.current
    if (state.dragId == null || !state.list) return
    const row = document.elementFromPoint(event.clientX, event.clientY)?.closest?.('[data-order-id]')
    if (!row) return
    const overId = Number(row.dataset.orderId)
    const from = state.list.findIndex((visit) => visit.id === state.dragId)
    const to = state.list.findIndex((visit) => visit.id === overId)
    if (from < 0 || to < 0 || from === to) return
    if (orderLocked(state.list[to]) || orderLocked(state.list[from])) return
    const rect = row.getBoundingClientRect()
    const midpoint = rect.top + rect.height / 2
    if (to > from && event.clientY < midpoint) return
    if (to < from && event.clientY > midpoint) return
    const next = [...state.list]
    const [item] = next.splice(from, 1)
    next.splice(to, 0, item)
    state.list = next
    setOrderDraft(next)
  }

  const onOrderPointerUp = async () => {
    const next = dragState.current.list
    dragState.current = { dragId: null, list: null }
    setDraggingId(null)
    setOrderDraft(null)
    if (next) await applyOrder(next)
  }

  const schoolCenterRef = useRef(null)
  const loadSchools = useCallback(async (center) => {
    if (!center) return
    const previous = schoolCenterRef.current
    if (previous && haversineKm(previous.lat, previous.lng, center.lat, center.lng) < 3) return
    schoolCenterRef.current = center
    try {
      const res = await api.get('/api/area-search/stored/', {
        params: { lat: center.lat, lng: center.lng, radius_km: 20 },
      })
      const rows = Array.isArray(res.data?.data) ? res.data.data : []
      setSchools(rows.filter((school) => Number.isFinite(Number(school.lat)) && Number.isFinite(Number(school.lng))))
    } catch {
      setSchools([])
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    const maps = window.google?.maps
    if (!map || !maps || !mapReady) return undefined
    const pull = () => {
      const center = map.getCenter()
      if (!center) return
      loadSchools({ lat: center.lat(), lng: center.lng() })
    }
    pull()
    const listener = map.addListener('idle', pull)
    return () => maps.event.removeListener(listener)
  }, [mapReady, loadSchools])

  const addVisitAt = useCallback(async (position, presetName) => {
    let areaName = String(presetName || '').trim()
    if (!areaName) {
      const { value: name } = await Swal.fire({
        title: 'New visit',
        input: 'text',
        inputLabel: 'School or place name',
        inputPlaceholder: 'Name',
        showCancelButton: true,
        confirmButtonText: 'Add visit',
      })
      areaName = String(name || '').trim()
    } else {
      const choice = await Swal.fire({
        title: 'Add this school?',
        text: areaName,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Add visit',
      })
      if (!choice.isConfirmed) return
    }
    if (!areaName) return
    setSaving(true)
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const nextOrder = myVisits.reduce((max, visit) => Math.max(max, Number(visit.route_order) || 0), 0) + 1
      await api.post('/visits/', {
        area_name: areaName,
        latitude: position.lat,
        longitude: position.lng,
        planned_date: todayIso(),
        status: 'planned',
        academic_year: academicYearRef.current || null,
        user_id: user?.id || undefined,
        route_order: nextOrder,
      })
      if (draftMarkerRef.current) {
        draftMarkerRef.current.setMap(null)
        draftMarkerRef.current = null
      }
      await loadVisits()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not add visit', text: err?.response?.data?.area_name?.[0] || 'Try again.' })
    } finally {
      setSaving(false)
    }
  }, [loadVisits, myVisits])

  const onSearchPlace = useCallback((place) => {
    const position = { lat: Number(place.lat), lng: Number(place.lng) }
    if (!Number.isFinite(position.lat) || !Number.isFinite(position.lng)) return
    const map = mapRef.current
    const maps = window.google?.maps
    if (map) {
      map.panTo(position)
      if ((map.getZoom() || 0) < 16) map.setZoom(16)
    }
    if (maps && map) {
      if (draftMarkerRef.current) draftMarkerRef.current.setMap(null)
      draftMarkerRef.current = new maps.Marker({
        map,
        position,
        title: place.name || 'New visit',
      })
    }
    loadSchools(position)
    addVisitAt(position, place.name || place.address)
  }, [addVisitAt, loadSchools])

  useEffect(() => {
    const map = mapRef.current
    const maps = window.google?.maps
    if (!map || !maps || !mapReady || !isMobile) return undefined
    const listener = map.addListener('click', (event) => {
      const position = { lat: event.latLng.lat(), lng: event.latLng.lng() }
      const nearStop = visitsRef.current.some((visit) => {
        const stop = stopPosition(visit)
        return stop && haversineKm(stop.lat, stop.lng, position.lat, position.lng) < 0.05
      })
      if (nearStop) return
      if (draftMarkerRef.current) draftMarkerRef.current.setMap(null)
      draftMarkerRef.current = new maps.Marker({
        map,
        position,
        title: 'New visit',
      })
      addVisitAt(position)
    })
    return () => maps.event.removeListener(listener)
  }, [mapReady, isMobile, addVisitAt])

  useEffect(() => {
    const map = mapRef.current
    const maps = window.google?.maps
    if (!map || !maps || !mapReady) return undefined
    schoolMarkersRef.current.forEach((marker) => marker.setMap(null))
    schoolMarkersRef.current = []
    const info = new maps.InfoWindow()
    const addedNames = new Set(visitsRef.current.map((visit) => schoolName(visit.area_name).toLowerCase()))
    schools.forEach((school) => {
      const lat = Number(school.lat)
      const lng = Number(school.lng)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
      const already = addedNames.has(String(school.name || '').trim().toLowerCase())
      const marker = new maps.Marker({
        map,
        position: { lat, lng },
        title: school.name,
        icon: already
          ? 'https://maps.google.com/mapfiles/ms/icons/green-dot.png'
          : 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
        zIndex: 1,
      })
      marker.addListener('click', () => {
        const position = { lat, lng }
        const action = already
          ? '<div style="margin-top:6px;color:#188038;font-size:12px;font-weight:600">Already added today</div>'
          : '<button id="unplanned-add-school" type="button" style="margin-top:8px;background:#1a73e8;color:#fff;border:0;border-radius:4px;padding:8px 12px;font-weight:600;cursor:pointer">Add visit</button>'
        info.setContent(`
          <div style="font-family:Roboto,Arial,sans-serif;max-width:220px">
            <div style="font-weight:700">${escapeHtml(school.name)}</div>
            <div style="color:#5f6368;font-size:12px;margin-top:2px">${escapeHtml(school.address || '')}</div>
            ${action}
          </div>`)
        info.open({ map, anchor: marker })
        if (!already) {
          maps.event.addListenerOnce(info, 'domready', () => {
            document.getElementById('unplanned-add-school')?.addEventListener('click', () => {
              info.close()
              addVisitAt(position, school.name)
            })
          })
        }
      })
      schoolMarkersRef.current.push(marker)
    })
    return () => {
      schoolMarkersRef.current.forEach((marker) => marker.setMap(null))
      schoolMarkersRef.current = []
    }
  }, [schools, visits, mapReady, addVisitAt])

  useEffect(() => {
    if (!isMobile || !navigator.geolocation) return undefined
    const active = visits.filter((visit) => visit.status === 'in_progress' || visit.status === 'riding_home')
    if (!active.length) return undefined
    const watchId = navigator.geolocation.watchPosition((position) => {
      const lat = position.coords.latitude
      const lng = position.coords.longitude
      active.forEach((visit) => {
        const home = visit.status === 'riding_home'
        const kmKey = home ? `visit_home_km_${visit.id}` : `visit_km_${visit.id}`
        const lastKey = home ? `visit_home_last_gps_${visit.id}` : `visit_last_gps_${visit.id}`
        const lastRaw = localStorage.getItem(lastKey)
        if (lastRaw) {
          try {
            const last = JSON.parse(lastRaw)
            const step = haversineKm(last.lat, last.lng, lat, lng)
            if (step < 0.025) return
            const total = parseFloat((parseFloat(localStorage.getItem(kmKey) || '0') + step).toFixed(2))
            localStorage.setItem(kmKey, String(total))
          } catch { /* ignore */ }
        }
        localStorage.setItem(lastKey, JSON.stringify({ lat, lng }))
        api.post('/visits/locations/', {
          visit: visit.id,
          latitude: lat.toFixed(7),
          longitude: lng.toFixed(7),
          accuracy: position.coords.accuracy,
          phase: home ? 'ride_home' : 'to_school',
        }).catch(() => {})
      })
    }, () => {}, { enableHighAccuracy: true, maximumAge: 3000, timeout: 10000 })
    return () => navigator.geolocation.clearWatch(watchId)
  }, [isMobile, visits])

  const startRide = async (visit) => {
    const blocking = visitsRef.current.filter((item) => (
      item.id !== visit.id && (item.status === 'in_progress' || item.status === 'at_school' || item.status === 'riding_home')
    ))
    if (visit.status === 'planned' && blocking.length) {
      Swal.fire({ icon: 'warning', title: 'Finish the current ride', text: blocking.map((item) => schoolName(item.area_name)).join(', ') })
      return
    }
    setSaving(true)
    try {
      const gps = await readGps()
      if (visit.status === 'planned') {
        localStorage.setItem(`visit_km_${visit.id}`, '0.0')
        localStorage.setItem(`visit_last_gps_${visit.id}`, JSON.stringify({ lat: Number(gps.latitude), lng: Number(gps.longitude) }))
      }
      if (visit.status === 'visited') {
        localStorage.setItem(`visit_home_km_${visit.id}`, '0.0')
        localStorage.setItem(`visit_home_last_gps_${visit.id}`, JSON.stringify({ lat: Number(gps.latitude), lng: Number(gps.longitude) }))
      }
      await api.post(`/visits/${visit.id}/start_visit/`, gps)
      await loadVisits()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not start', text: err?.response?.data?.error || err.message })
    } finally {
      setSaving(false)
    }
  }

  const closeCamera = (photo) => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    const pending = photoRequestRef.current
    photoRequestRef.current = null
    setCameraOpen(false)
    setCapturedPhoto(null)
    setCameraVisit(null)
    setCameraError('')
    pending?.resolve(photo)
  }

  const askPhoto = (visit, purpose) => new Promise((resolve) => {
    photoRequestRef.current = { resolve }
    cameraGpsRef.current = {}
    setCameraVisit(visit)
    setCameraPurpose(purpose)
    setCapturedPhoto(null)
    setCameraError('')
    setCameraOpen(true)
    readGps().then((gps) => {
      cameraGpsRef.current = gps
      setCapturedPhoto((current) => (
        current ? { ...current, latitude: gps.latitude, longitude: gps.longitude } : current
      ))
    }).catch(() => {})
  })

  useEffect(() => {
    if (!cameraOpen || capturedPhoto) return undefined
    let cancelled = false
    navigator.mediaDevices?.getUserMedia?.({ video: { facingMode: { ideal: 'environment' } }, audio: false })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }
        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          videoRef.current.play().catch(() => {})
        }
      })
      .catch(() => {
        if (!cancelled) setCameraError('Camera is unavailable. Choose an image instead.')
      })
    return () => {
      cancelled = true
      streamRef.current?.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
  }, [cameraOpen, capturedPhoto])

  const rememberPhoto = (dataUrl, lat, lng) => {
    const blob = dataUrlToBlob(dataUrl)
    setCapturedPhoto({
      dataUrl,
      blob,
      takenAt: new Date().toISOString(),
      latitude: lat,
      longitude: lng,
    })
  }

  const takePhoto = () => {
    const video = videoRef.current
    if (!video || !video.videoWidth) return
    const canvas = document.createElement('canvas')
    const scale = Math.min(800 / video.videoWidth, 1)
    canvas.width = video.videoWidth * scale
    canvas.height = video.videoHeight * scale
    const ctx = canvas.getContext('2d')
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    const gps = cameraGpsRef.current || {}
    const when = new Date()
    const caption = [
      cameraPurpose === 'school_end' ? 'End visit' : 'Reached school',
      schoolName(cameraVisit?.area_name),
      (gps.latitude != null && gps.longitude != null) ? `${Number(gps.latitude).toFixed(6)}, ${Number(gps.longitude).toFixed(6)}` : '',
      when.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }),
    ].filter(Boolean)
    stampPhotoCaption(ctx, canvas.width, canvas.height, caption)
    rememberPhoto(canvas.toDataURL('image/jpeg', 0.6), gps.latitude, gps.longitude)
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }

  const onPickImage = (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const gps = cameraGpsRef.current || {}
      rememberPhoto(String(reader.result || ''), gps.latitude, gps.longitude)
    }
    reader.readAsDataURL(file)
  }

  const uploadVisitPhoto = async (visit, photo, purpose) => {
    const file = new File(
      [photo.blob],
      `${purpose}_${Date.now()}.jpg`,
      { type: 'image/jpeg' },
    )
    const formData = new FormData()
    formData.append('visit', visit.id)
    formData.append('file', file)
    formData.append('caption', purpose === 'school_end' ? 'School end photo' : 'Ride end / arrival photo')
    formData.append('media_type', 'photo')
    formData.append('location_name', schoolName(visit.area_name))
    if (photo.latitude != null) formData.append('latitude', Number(photo.latitude).toFixed(7))
    if (photo.longitude != null) formData.append('longitude', Number(photo.longitude).toFixed(7))
    await api.post('/media/', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    return file
  }

  const shareVisitPhoto = async (visit, photo, purpose) => {
    const when = photo.takenAt ? new Date(photo.takenAt) : new Date()
    const label = purpose === 'school_end' ? 'End visit' : 'Reached school'
    const lines = [`${label}: ${schoolName(visit.area_name)}`]
    if (photo.latitude != null && photo.longitude != null) {
      lines.push(`Coordinates: ${Number(photo.latitude).toFixed(6)}, ${Number(photo.longitude).toFixed(6)}`)
    }
    const mapUrl = mapsQueryUrl(photo.latitude, photo.longitude, schoolName(visit.area_name))
    if (mapUrl) lines.push(`Map: ${mapUrl}`)
    lines.push(`Date & time: ${when.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`)
    const file = new File([photo.blob], `${purpose}_${Date.now()}.jpg`, { type: 'image/jpeg' })
    await shareOnWhatsApp({ file, text: lines.join('\n'), title: label })
  }

  const reachedSchool = async (visit) => {
    const transport = await askTransport('Reached school')
    if (!transport) return
    const photo = await askPhoto(visit, 'ride_end')
    if (!photo) return
    setSaving(true)
    try {
      if (photo.blob) {
        await uploadVisitPhoto(visit, photo, 'ride_end')
        try {
          await shareVisitPhoto(visit, photo, 'ride_end')
        } catch { /* the photo is already saved on the visit */ }
      }
      const distance = parseFloat(localStorage.getItem(`visit_km_${visit.id}`) || '0')
      await api.post(`/visits/${visit.id}/end_visit/`, {
        ...transport,
        distance_km: distance,
        auto_start_school: true,
      })
      localStorage.removeItem(`visit_km_${visit.id}`)
      localStorage.removeItem(`visit_last_gps_${visit.id}`)
      await loadVisits()
      Swal.fire({ icon: 'success', title: 'School time started', timer: 1400, showConfirmButton: false })
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not save', text: err?.response?.data?.error || 'Try again.' })
    } finally {
      setSaving(false)
    }
  }

  const endSchool = async (visit) => {
    const photo = await askPhoto(visit, 'school_end')
    if (!photo) return
    setSaving(true)
    try {
      if (photo.blob) await uploadVisitPhoto(visit, photo, 'school_end')
      const gps = await readGps().catch(() => ({}))
      await api.post(`/visits/${visit.id}/end_visit/`, gps)
      await loadVisits()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not end visit', text: err?.response?.data?.error || 'Try again.' })
    } finally {
      setSaving(false)
    }
  }

  const reachedHome = async (visit) => {
    const transport = await askTransport('Reached home')
    if (!transport) return
    setSaving(true)
    try {
      const gps = await readGps().catch(() => ({}))
      const distance = parseFloat(localStorage.getItem(`visit_home_km_${visit.id}`) || '0')
      await api.post(`/visits/${visit.id}/end_visit/`, {
        ...transport,
        ...gps,
        distance_km: distance,
      })
      localStorage.removeItem(`visit_home_km_${visit.id}`)
      localStorage.removeItem(`visit_home_last_gps_${visit.id}`)
      await loadVisits()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not save', text: err?.response?.data?.error || 'Try again.' })
    } finally {
      setSaving(false)
    }
  }

  const showCurrentLocation = () => {
    if (!navigator.geolocation) {
      setMapError('Location is not available on this device.')
      return
    }
    setLocating(true)
    setMapError('')
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const point = { lat: position.coords.latitude, lng: position.coords.longitude }
        const map = mapRef.current
        const maps = window.google?.maps
        if (map) {
          map.panTo(point)
          if ((map.getZoom() || 0) < 15) map.setZoom(15)
        }
        if (maps && map) {
          if (!hereMarkerRef.current) {
            hereMarkerRef.current = new maps.Marker({
              map,
              position: point,
              zIndex: 2000,
              title: 'Live location',
              icon: liveLocationIcon(maps),
            })
          } else {
            hereMarkerRef.current.setMap(map)
            hereMarkerRef.current.setPosition(point)
          }
        }
        loadSchools(point)
        setLocating(false)
      },
      () => {
        setMapError('Could not get your current location. Allow location access and try again.')
        setLocating(false)
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
    )
  }

  const currentLocationButton = (
    <button
      type="button"
      onClick={showCurrentLocation}
      disabled={locating}
      style={{
        position: 'absolute',
        right: 12,
        bottom: 12,
        zIndex: 2,
        border: 0,
        borderRadius: 8,
        background: '#fff',
        color: '#1a73e8',
        boxShadow: '0 1px 4px rgba(0,0,0,.3)',
        padding: '10px 12px',
        fontWeight: 700,
        fontSize: 13,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        cursor: locating ? 'wait' : 'pointer',
      }}
    >
      <MapPin size={14} />
      {locating ? 'Locating...' : 'Current location'}
    </button>
  )

  const actionButton = {
    border: 0,
    borderRadius: 8,
    padding: '10px 12px',
    fontWeight: 700,
    fontSize: 13,
    color: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  }

  const renderRideActions = (visit) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
      {visit.status === 'planned' && (
        <button type="button" disabled={saving} style={{ ...actionButton, background: '#16a34a' }} onClick={() => startRide(visit)}>
          <Play size={14} /> Start Ride
        </button>
      )}
      {visit.status === 'in_progress' && (
        <button type="button" disabled={saving} style={{ ...actionButton, background: '#d97706' }} onClick={() => reachedSchool(visit)}>
          <CheckCircle size={14} /> Reached School
        </button>
      )}
      {visit.status === 'arrived' && (
        <button type="button" disabled={saving} style={{ ...actionButton, background: '#16a34a' }} onClick={() => startRide(visit)}>
          <Play size={14} /> Start School
        </button>
      )}
      {visit.status === 'at_school' && (
        <button type="button" disabled={saving} style={{ ...actionButton, background: '#d97706' }} onClick={() => endSchool(visit)}>
          <CheckCircle size={14} /> End Visit
        </button>
      )}
      {visit.status === 'visited' && !visit.home_end_time && (
        <button type="button" disabled={saving} style={{ ...actionButton, background: '#16a34a' }} onClick={() => startRide(visit)}>
          <Play size={14} /> Ride Home
        </button>
      )}
      {visit.status === 'riding_home' && (
        <button type="button" disabled={saving} style={{ ...actionButton, background: '#d97706' }} onClick={() => reachedHome(visit)}>
          <CheckCircle size={14} /> Reached Home
        </button>
      )}
    </div>
  )

  const locationSearch = (
    <div style={{ position: 'absolute', left: 12, top: 12, right: 12, zIndex: 2, maxWidth: 360 }}>
      <LocationSearch onPlace={onSearchPlace} />
    </div>
  )

  const cameraModal = cameraOpen ? (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(15,23,42,.72)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}
    >
      <div style={{ width: '100%', maxWidth: 420, background: '#0f172a', color: '#fff', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px 8px' }}>
          <div style={{ fontWeight: 700 }}>
            {cameraPurpose === 'school_end' ? 'End visit photo' : 'Reached school photo'}
          </div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>{schoolName(cameraVisit?.area_name)}</div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Photo is optional. You can skip and continue.</div>
        </div>
        {capturedPhoto ? (
          <img src={capturedPhoto.dataUrl} alt="Visit" style={{ width: '100%', maxHeight: 320, objectFit: 'cover', display: 'block' }} />
        ) : (
          <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', maxHeight: 320, background: '#000', display: cameraError ? 'none' : 'block' }} />
        )}
        {cameraError && !capturedPhoto && (
          <div style={{ padding: '12px 16px', fontSize: 13, color: '#fcd34d' }}>{cameraError}</div>
        )}
        <div style={{ display: 'flex', gap: 8, padding: 12, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => closeCamera(null)} style={{ ...actionButton, background: '#475569' }}>
            Cancel
          </button>
          <button type="button" onClick={() => closeCamera({ skipped: true })} style={{ ...actionButton, background: '#0f766e' }}>
            Skip photo
          </button>
          {!capturedPhoto && (
            <button type="button" onClick={takePhoto} style={{ ...actionButton, background: '#1a73e8' }}>
              <Camera size={14} /> Take photo
            </button>
          )}
          <button type="button" onClick={() => fileInputRef.current?.click()} style={{ ...actionButton, background: '#334155' }}>
            Choose image
          </button>
          {capturedPhoto && (
            <>
              <button type="button" onClick={() => setCapturedPhoto(null)} style={{ ...actionButton, background: '#b91c1c' }}>
                Retake
              </button>
              <button type="button" onClick={() => closeCamera(capturedPhoto)} style={{ ...actionButton, background: '#16a34a' }}>
                Use this photo
              </button>
            </>
          )}
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" capture="environment" hidden onChange={onPickImage} />
      </div>
    </div>
  ) : null

  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100dvh - 64px)', minHeight: 480 }}>
        <div style={{ position: 'relative', flex: 1, minHeight: 220 }}>
          <div ref={mapNodeRef} style={{ width: '100%', height: '100%' }} />
          {locationSearch}
          {currentLocationButton}
          {cameraModal}
          {(mapError || error) && (
            <div style={{ position: 'absolute', left: 12, bottom: 12, zIndex: 2, background: '#fff', padding: '8px 12px', borderRadius: 8, fontSize: 13 }}>
              {mapError || error}
            </div>
          )}
        </div>
        <div style={{ background: '#fff', borderTop: '1px solid #e2e8f0', padding: '10px 12px 14px' }}>
          {(() => {
            const selectedIndex = myVisits.findIndex((visit) => visit.id === selectedStopId)
            const selected = selectedIndex >= 0 ? myVisits[selectedIndex] : null
            if (!selected) return null
            return (
              <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{schoolName(selected.area_name)}</div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                  Stop {selectedIndex + 1}
                  {' · '}
                  {formatTime(selected.start_time || selected.school_arrival_time) || 'Not started'}
                  {selected.status ? ` · ${selected.status.replace(/_/g, ' ')}` : ''}
                </div>
                {renderRideActions(selected)}
              </div>
            )
          })()}
          {myVisits.length === 0 && !loading ? (
            <div style={{ color: '#64748b', fontSize: 14 }}>No visits yet. Mark one on the map.</div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 2, flex: 1 }}>
              {myVisits.map((visit, index) => {
                const active = visit.id === selectedStopId
                return (
                  <button
                    key={visit.id}
                    type="button"
                    aria-label={`Stop ${index + 1}`}
                    onClick={() => setSelectedStopId(active ? null : visit.id)}
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      border: active ? '3px solid #1e3a8a' : '0',
                      background: active ? '#1d4ed8' : '#1a73e8',
                      color: '#fff',
                      fontWeight: 800,
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </button>
                )
              })}
            </div>
            <button
              type="button"
              onClick={() => setOrderOpen(true)}
              style={{
                border: 0,
                background: '#1a73e8',
                color: '#fff',
                borderRadius: 10,
                padding: '12px 14px',
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              Order
            </button>
            </div>
          )}
          {orderOpen && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(15, 23, 42, 0.45)',
                zIndex: 30,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
              }}
              onClick={() => setOrderOpen(false)}
            >
              <div
                style={{
                  background: '#fff',
                  width: '100%',
                  maxWidth: 480,
                  maxHeight: '70vh',
                  overflow: 'auto',
                  borderRadius: '16px 16px 0 0',
                  padding: '16px 16px 24px',
                }}
                onClick={(event) => event.stopPropagation()}
              >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                  <strong style={{ fontSize: 16 }}>Change order</strong>
                  <button type="button" onClick={() => setOrderOpen(false)} style={{ marginLeft: 'auto', border: 0, background: 'transparent', fontWeight: 700 }}>
                    Close
                  </button>
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>Drag a stop to change its place in the route.</div>
                {orderList.map((visit, index) => {
                  const locked = orderLocked(visit)
                  const prevLocked = index === 0 || orderLocked(orderList[index - 1])
                  const nextLocked = index === orderList.length - 1 || orderLocked(orderList[index + 1])
                  return (
                    <div
                      key={visit.id}
                      data-order-id={visit.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #e2e8f0',
                        background: draggingId === visit.id ? '#eff6ff' : 'transparent',
                        borderRadius: draggingId === visit.id ? 8 : 0,
                        pointerEvents: draggingId === visit.id ? 'none' : 'auto',
                      }}
                    >
                      <button
                        type="button"
                        aria-label="Drag to reorder"
                        disabled={locked || saving}
                        onPointerDown={(event) => onOrderPointerDown(event, visit)}
                        onPointerMove={onOrderPointerMove}
                        onPointerUp={onOrderPointerUp}
                        onPointerCancel={onOrderPointerUp}
                        style={{
                          border: 0, background: 'transparent', padding: 4, cursor: locked ? 'default' : 'grab',
                          touchAction: 'none', opacity: locked ? 0.35 : 1, display: 'inline-flex',
                        }}
                      >
                        <GripVertical size={18} />
                      </button>
                      <span style={{
                        width: 28, height: 28, borderRadius: '50%', background: locked ? '#94a3b8' : '#1a73e8', color: '#fff',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0,
                      }}
                      >
                        {index + 1}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700 }}>{schoolName(visit.area_name)}</div>
                        {locked && <div style={{ fontSize: 12, color: '#64748b' }}>Visited — order is fixed</div>}
                      </div>
                      <button
                        type="button"
                        aria-label="Move earlier"
                        disabled={locked || prevLocked || saving}
                        onClick={() => moveVisit(index, -1)}
                        style={{ border: 0, background: '#f1f5f9', borderRadius: 8, padding: 8, opacity: locked || prevLocked ? 0.4 : 1 }}
                      >
                        <ChevronUp size={18} />
                      </button>
                      <button
                        type="button"
                        aria-label="Move later"
                        disabled={locked || nextLocked || saving}
                        onClick={() => moveVisit(index, 1)}
                        style={{ border: 0, background: '#f1f5f9', borderRadius: 8, padding: 8, opacity: locked || nextLocked ? 0.4 : 1 }}
                      >
                        <ChevronDown size={18} />
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 88px)', minHeight: 480 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 4px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Route size={18} />
          Unplanned
        </h2>
        <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          Today’s visits with the travel line · blue dot is your live location
        </span>
        {isAdmin && (
          <select
            value={personId}
            onChange={(event) => setPersonId(event.target.value)}
            style={{ marginLeft: 'auto', height: 36, borderRadius: 8, border: '1px solid var(--border-color, #e2e8f0)', padding: '0 10px' }}
          >
            <option value="">All sales persons</option>
            {persons.map((person) => (
              <option key={person.id} value={person.id}>{person.name}</option>
            ))}
          </select>
        )}
      </div>

      <div style={{ display: 'flex', flex: 1, minHeight: 0, gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0, position: 'relative', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border-color, #e2e8f0)' }}>
          <div ref={mapNodeRef} style={{ width: '100%', height: '100%' }} />
          {locationSearch}
          {currentLocationButton}
          {cameraModal}
          {(mapError || loading || error) && (
            <div style={{
              position: 'absolute', left: 12, bottom: 12, zIndex: 2,
              background: '#fff', padding: '8px 12px', borderRadius: 8,
              boxShadow: '0 1px 4px rgba(0,0,0,.2)', fontSize: 13,
            }}
            >
              {mapError || error || 'Loading today’s visits…'}
            </div>
          )}
        </div>

        <div style={{ width: 300, overflow: 'auto', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: 12, background: '#fff' }}>
          {grouped.length === 0 && !loading ? (
            <div style={{ padding: 20, color: 'var(--text-muted)', fontSize: 14 }}>
              <MapPin size={18} />
              <div style={{ marginTop: 8 }}>No visits for today.</div>
            </div>
          ) : grouped.map((group) => (
            <div key={group.personId || group.personName} style={{ borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ padding: '12px 14px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: group.color }} />
                {group.personName}
              </div>
              {group.visits.map((visit, index) => (
                <div key={visit.id} style={{ padding: '8px 14px 12px', display: 'flex', gap: 10 }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: '50%', background: group.color, color: '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0,
                  }}
                  >
                    {index + 1}
                  </span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{schoolName(visit.area_name)}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                      {formatTime(visit.start_time || visit.school_arrival_time) || 'Not started'}
                      {visit.status ? ` · ${visit.status.replace(/_/g, ' ')}` : ''}
                    </div>
                    {renderRideActions(visit)}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
